import { isEmpty } from "lodash";
import { Link } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useThemeContext } from "@/react/providers";
import { shouldRenderLockIcon } from "@circle-react/components/Spaces/utils";
import { Icon } from "@circle-react/components/shared/Icon";
import {
  usePunditUserContext,
  useSpaceGroupsContext,
  useSpacesContext,
} from "@circle-react/contexts";
import { useActiveSpaceMembersCount } from "@circle-react/contexts/useActiveSpaceMembersCount";
import { fetchRequest } from "@circle-react/helpers/backendRequestHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { toLocaleString } from "@circle-react/helpers/number";
import { spaceAccessViaPaywall } from "@circle-react/helpers/spaceHelpers";
import {
  internalApi,
  newSessionPath,
  spaces,
} from "@circle-react/helpers/urlHelpers";
import type { Space } from "@circle-react/types";
import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { Loader } from "@circle-react-uikit/Loader";
import { SpaceCardButtons } from "./SpaceCardButtons";

export interface SpaceCardProps {
  space?: Space;
}

export const SpaceCard = ({ space }: SpaceCardProps) => {
  const { currentAppearance } = useThemeContext();
  const { currentCommunityMember, currentUser, isViewOnlyMasquerading } =
    usePunditUserContext();
  const {
    actions: { refresh: refreshSpaceGroups },
  } = useSpaceGroupsContext();
  const {
    actions: { refresh: refreshSpaces },
  } = useSpacesContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const {
    count: activeSpaceMembersCount,
    isLoading,
    refetch: refetchActiveSpaceMembersCount,
  } = useActiveSpaceMembersCount({
    spaceId: space?.id,
    enabled: !space?.hide_members_count,
  });

  if (!space || isEmpty(space)) return null;

  const {
    id,
    emoji,
    custom_emoji_url: customEmojiUrl,
    custom_emoji_dark_url: customEmojiDarkUrl,
    name,
    hide_members_count: shouldHideMembersCount,
    slug,
    is_space_member: isSpaceMember,
    is_private: isPrivate,
    locked_button_url: lockedButtonURL,
    locked_button_label: lockedButtonLabel,
    show_lock_icon_for_non_members: shouldShowLockIconForNonMembers,
  } = space;

  const refreshData = () => {
    void refreshSpaceGroups();
    void refreshSpaces();
    void refetchActiveSpaceMembersCount();
  };

  const shouldUseLockIcon = shouldRenderLockIcon({
    shouldShowLockIconForNonMembers:
      shouldShowLockIconForNonMembers && !isSpaceMember,
    isPrivate,
    isAdmin,
  });

  const joinSpace = async (): Promise<void> => {
    if (!currentCommunityMember) {
      window.location.assign(newSessionPath());
      return;
    }
    await fetchRequest(internalApi.spaceMembers.joinSpace({ spaceId: id }), {
      method: "POST",
    });
    refreshData();
  };

  if (!space || !currentUser) {
    return null;
  }

  return (
    <div className="space-card !rounded-2xl">
      <div className="space-card__inside flex h-full flex-col justify-between">
        <Link
          to={spaces.show({ spaceSlug: slug })}
          className="space-card__name"
        >
          <div className="space-card__icon">
            <EmojiRenderer
              className="title-emoji mx-auto !h-10 !max-h-10 !w-10"
              emoji={emoji}
              appearance={currentAppearance}
              customEmojiUrl={customEmojiUrl}
              customEmojiDarkUrl={customEmojiDarkUrl}
              shouldUseLockIcon={shouldUseLockIcon}
              lockIcon={<Icon type="lock" />}
            />
          </div>
          <div className="space-card__title truncate">
            <span className="title-name">{name}</span>
          </div>
        </Link>
        {!shouldHideMembersCount &&
          (isLoading ? (
            <Loader />
          ) : (
            <div className="space-card__members">
              {toLocaleString(activeSpaceMembersCount)}{" "}
              {activeSpaceMembersCount === 1 ? t("member") : t("members")}
            </div>
          ))}
        <SpaceCardButtons
          currentUser={currentUser}
          isSpaceMember={Boolean(isSpaceMember)}
          isPrivate={Boolean(isPrivate)}
          isAdmin={isAdmin}
          lockedButtonLabel={lockedButtonLabel ?? ""}
          lockedButtonURL={lockedButtonURL ?? ""}
          joinSpace={() => void joinSpace()}
          spaceAccessViaPaywall={spaceAccessViaPaywall(space)}
          space={space}
          isViewOnlyMasquerading={isViewOnlyMasquerading}
        />
      </div>
    </div>
  );
};
